import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { TrendingUp, TrendingDown, Wallet, Coins, Banknote } from "lucide-react";
import { api } from "../../lib/api";
import { Card, PageHeader, Spinner, EmptyState } from "../../components/dashboard/DashboardShell";
import { Button } from "../../components/ui/button";
import { rupiah, fmtDate, fmtDateTime } from "../../lib/format";

const LEDGER_META = {
  release: ["Dana masuk", "text-emerald-600"], payout_request: ["Pencairan", "text-slate-700"], payout_rejected: ["Pencairan ditolak (refund saldo)", "text-amber-600"], payout_paid: ["Pencairan dibayar", "text-slate-500"],
};

export default function EarningsPage() {
  const navigate = useNavigate();
  const { data: dash, isLoading } = useQuery({ queryKey: ["inspector-dashboard"], queryFn: () => api.get("/inspectors/me/dashboard").then((r) => r.data) });
  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data) });
  if (isLoading || !dash) return <Spinner />;
  const monthly = dash.earnings.monthly.map((m) => ({ ...m, bulan: fmtDate(m.bulan + "-01", "MMM yy") }));
  const up = dash.earnings.growth_percent >= 0;
  return (
    <div>
      <PageHeader title="Pendapatan" subtitle="Ringkasan pendapatan dari inspeksi yang telah selesai."
        action={<Button onClick={() => navigate("/inspector/pencairan")} className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-10 px-4 text-sm"><Banknote size={15} className="mr-1.5" /> Tarik Dana</Button>} />
      <div className="grid sm:grid-cols-3 gap-4">
        <Card><div className="flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Coins size={18} /></span><p className="text-xs font-semibold text-slate-400">Pendapatan Bulan Ini</p></div>
          <p className="text-2xl font-extrabold text-slate-900 mt-3" data-testid="earnings-this-month">{rupiah(dash.earnings.this_month)}</p>
          <p className={`text-[11px] font-bold flex items-center gap-1 mt-1 ${up ? "text-emerald-500" : "text-red-500"}`}>{up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {Math.abs(dash.earnings.growth_percent)}% dari bulan lalu</p></Card>
        <Card><div className="flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Wallet size={18} /></span><p className="text-xs font-semibold text-slate-400">Saldo Tersedia</p></div>
          <p className="text-2xl font-extrabold text-slate-900 mt-3" data-testid="earnings-balance">{rupiah(dash.balance)}</p><p className="text-[11px] text-slate-400 mt-1">Siap dicairkan</p></Card>
        <Card><div className="flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><Banknote size={18} /></span><p className="text-xs font-semibold text-slate-400">Total Pendapatan</p></div>
          <p className="text-2xl font-extrabold text-slate-900 mt-3">{rupiah(wallet?.total_earned)}</p><p className="text-[11px] text-slate-400 mt-1">Sudah dicairkan {rupiah(wallet?.total_paid_out)}</p></Card>
      </div>
      <div className="grid xl:grid-cols-3 gap-4 mt-4">
        <Card title="Grafik Pendapatan (6 bulan)" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} barSize={34}>
                <XAxis dataKey="bulan" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={40} />
                <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="total" radius={[8, 8, 8, 8]}>{monthly.map((e, i) => <Cell key={i} fill={i === monthly.length - 1 ? "#2563eb" : "#93c5fd"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Mutasi Saldo">
          {!wallet?.ledger?.length ? <EmptyState icon="wallet" title="Belum ada mutasi" /> : (
            <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto" data-testid="ledger-list">
              {wallet.ledger.slice(0, 15).map((l, i) => {
                const [lbl, cls] = LEDGER_META[l.type] || [l.type, "text-slate-600"];
                return (
                  <div key={i} className="py-2.5 flex items-center justify-between gap-2">
                    <div className="min-w-0"><p className="text-[13px] font-semibold text-slate-700 truncate">{lbl}{l.order_id ? ` · ${l.order_id}` : ""}</p><p className="text-[11px] text-slate-400">{fmtDateTime(l.created_at)}</p></div>
                    <span className={`text-[13px] font-extrabold whitespace-nowrap ${cls}`}>{l.amount > 0 ? "+" : ""}{l.amount ? rupiah(l.amount) : "-"}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
